// v2.19.38 — Vue "Mon Compte Pro" (Sprint 1 skeleton).
// Charge GET /api/v3/me/pro/bank et affiche :
//  - Hero carte virtuelle Bridge Bank Group (dégradé bleu marine + or)
//  - 3 cards placeholder (Solde / Commandes / Stats)
//  - Section "Mon adresse pro" (claim Maison X)
//  - Footer roadmap sprints v2.19.39 / v2.19.40
//
// Auth : si 401/403 → message d'accès refusé (un user role=user qui force ?tab=pro voit ça).

import { useEffect, useState } from "react";
import { toast } from "sonner";
import MiniAddressMap from "@/components/MiniAddressMap";
// [H_UIPOLISH_V1_L3 sous-palier 2.B] Nouveau dashboard polish-only,
// remplace UNIQUEMENT le bloc 3 PlaceholderCard (placeholder visuel).
import MerchantProDashboard from "@/components/MerchantProDashboard";
import MiniSiteEditor from "@/components/MiniSiteEditor";
import ProLeadsSection from "@/components/ProLeadsSection";
import ProBookingsSection from "@/components/ProBookingsSection";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

function fmtFcfa(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

function BridgeBankLogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
        <defs>
          <linearGradient id="bbg-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#A07C20" />
          </linearGradient>
        </defs>
        <rect x="1" y="1" width="26" height="26" rx="6" fill="#0F1A2E" stroke="url(#bbg-grad)" strokeWidth="1.4" />
        <path d="M7 14 L14 7 L21 14 L17.5 14 L17.5 20 L10.5 20 L10.5 14 Z" fill="url(#bbg-grad)" />
      </svg>
      <div style={{ lineHeight: 1.1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#D4AF37", letterSpacing: 1.2 }}>BRIDGE BANK</div>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.55)", letterSpacing: 1.5 }}>GROUP</div>
      </div>
    </div>
  );
}

function VirtualCard({ card, holder, iban, onRevealCvv, cvvRevealed, onCopyIban }) {
  return (
    <div
      data-testid="bridge-virtual-card"
      style={{
        position: "relative",
        borderRadius: 18,
        padding: "22px 22px 18px",
        background: "linear-gradient(135deg, #0F1A2E 0%, #1B2E54 55%, #2A4173 100%)",
        boxShadow: "0 18px 48px rgba(15, 26, 46, 0.55), inset 0 1px 0 rgba(212, 175, 55, 0.25)",
        border: "1px solid rgba(212, 175, 55, 0.35)",
        color: "#F4F1E8",
        overflow: "hidden",
        minHeight: 200,
      }}
    >
      {/* glow gold subtle top-right */}
      <div style={{
        position: "absolute", top: -40, right: -40, width: 160, height: 160,
        background: "radial-gradient(circle, rgba(212,175,55,0.22), transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, position: "relative" }}>
        <BridgeBankLogo />
        <div style={{
          fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
          background: "linear-gradient(135deg, rgba(212,175,55,0.25), rgba(212,175,55,0.08))",
          color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)", letterSpacing: 1,
        }}>
          {card?.type === "virtual_premium" ? "PREMIUM" : "STANDARD"}
        </div>
      </div>

      <div data-testid="card-number" style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 19, letterSpacing: 2, marginBottom: 18,
        color: "#F4F1E8", textShadow: "0 1px 2px rgba(0,0,0,0.4)",
      }}>
        {card?.number_masked || "•••• •••• •••• ••••"}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: 11, gap: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "rgba(244,241,232,0.55)", fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>TITULAIRE</div>
          <div style={{ fontWeight: 700, letterSpacing: 0.5 }}>{(card?.holder || holder || "—").toUpperCase()}</div>
        </div>
        <div>
          <div style={{ color: "rgba(244,241,232,0.55)", fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>EXP.</div>
          <div style={{ fontWeight: 700 }}>{card?.expiry || "—"}</div>
        </div>
        <div>
          <div style={{ color: "rgba(244,241,232,0.55)", fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>CVV</div>
          <button
            data-testid="reveal-cvv-btn"
            onClick={onRevealCvv}
            style={{
              background: "transparent", color: "#D4AF37", border: "1px dashed rgba(212,175,55,0.4)",
              padding: "2px 8px", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 11,
            }}
          >
            {cvvRevealed ? (card?.cvv || "—") : "•••"}
          </button>
        </div>
      </div>

      {iban && (
        <div style={{
          marginTop: 16, paddingTop: 12, borderTop: "1px dashed rgba(244,241,232,0.18)",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ color: "rgba(244,241,232,0.55)", fontSize: 9, letterSpacing: 1, marginBottom: 2 }}>IBAN</div>
            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: 1 }}>{iban}</div>
          </div>
          <button
            data-testid="copy-iban-btn"
            onClick={onCopyIban}
            style={{
              background: "rgba(212,175,55,0.15)", color: "#D4AF37",
              border: "1px solid rgba(212,175,55,0.35)", padding: "5px 12px",
              borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 11,
            }}
          >📋 Copier</button>
        </div>
      )}
    </div>
  );
}

function PlaceholderCard({ icon, title, sprint }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.04)",
      border: "1px dashed rgba(212,175,55,0.22)",
      borderRadius: 14,
      padding: 18,
      textAlign: "left",
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#F4F1E8", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: "rgba(244,241,232,0.5)", fontStyle: "italic" }}>
        Arrive en {sprint}
      </div>
    </div>
  );
}

// v3.0.0-rc.8u — AYA Live : raccourci commerçant vers /pro/live (dashboard live commerce)
function AyaLiveShortcut() {
  return (
    <a
      href="/pro/live"
      data-testid="pro-aya-live-shortcut"
      style={{
        display: "block",
        textDecoration: "none",
        background: "linear-gradient(135deg, rgba(239,68,68,0.14), rgba(255,126,27,0.14))",
        border: "1px solid rgba(239,68,68,0.4)",
        borderRadius: 16,
        padding: 18,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #EF4444, #FF7E1B)",
          fontSize: 24, flexShrink: 0,
        }}>🎥</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#F4F1E8" }}>AYA Live</div>
            <span data-testid="pro-aya-live-badge" style={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.6,
              padding: "2px 6px", borderRadius: 4,
              background: "rgba(239,68,68,0.25)", color: "#FCA5A5",
            }}>LIVE COMMERCE</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(244,241,232,0.7)", lineHeight: 1.5 }}>
            Lance ton live TikTok/Instagram/FB avec un QR overlay qui convertit les viewers en clients. Debrief IA après chaque session.
          </div>
        </div>
        <div style={{ fontSize: 22, color: "rgba(244,241,232,0.5)", flexShrink: 0 }}>›</div>
      </div>
    </a>
  );
}

function ProAddressSection({ addr }) {
  if (!addr) {
    return (
      <div data-testid="pro-address-empty" style={{
        background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)",
        borderRadius: 12, padding: 16, color: "#94a3b8", fontSize: 12,
      }}>
        Aucune adresse pro déclarée. Revendique une adresse Maison Koffi pour qu&apos;elle s&apos;affiche ici.
      </div>
    );
  }
  return (
    <div data-testid="pro-address-block" style={{
      background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))",
      border: "1px solid rgba(212,175,55,0.25)",
      borderRadius: 14, padding: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>🏷</span>
        <div style={{ fontWeight: 700, color: "#D4AF37", letterSpacing: 0.5 }}>{addr.friendly_name}</div>
      </div>
      <div style={{ fontSize: 12, color: "rgba(244,241,232,0.7)", lineHeight: 1.5, marginBottom: 12 }}>
        Adresse CIV : <code style={{ fontWeight: 700, color: "#FBBF24" }}>{addr.address_civ}</code><br />
        Commune : {addr.commune}<br />
        Visibilité : {addr.is_public ? "🌐 Publique (clients peuvent te trouver)" : "🔒 Privée"}
      </div>
      {/* v2.19.38b — Mini-carte précise avec pin or sur l'adresse pro */}
      <MiniAddressMap lat={addr.lat} lng={addr.lng} label={addr.friendly_name} />
    </div>
  );
}

// v3.6.1 — Section uniforme : chaque bloc de l'espace pro vit dans une carte
// titrée cohérente (request user : « ranger les cartes pour un visuel propre »).
function ProSection({ icon, title, subtitle, action, children, testid }) {
  return (
    <section data-testid={testid} style={{
      background: "linear-gradient(180deg, rgba(6,42,31,0.42), rgba(4,24,15,0.28))",
      border: "1px solid rgba(232,213,163,0.16)",
      borderRadius: 16,
      padding: "14px 16px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }} aria-hidden>{icon}</span>
            <h3 style={{ margin: 0, fontSize: 14.5, color: "#F4F1E8", letterSpacing: 0.3 }}>{title}</h3>
          </div>
          {subtitle && <div style={{ fontSize: 11, color: "rgba(244,241,232,0.5)", marginTop: 3 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </section>
  );
}

export default function MonComptePro() {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cvvRevealed, setCvvRevealed] = useState(false);
  // v3.4.0 — config domain-aware (avocat/fleuriste/hôtelier/etc.)
  const [spaceConfig, setSpaceConfig] = useState(null);
  // v3.6.1 — slug boutique publique (connexion espace pro ↔ /commercant/<slug>)
  const [shopSlug, setShopSlug] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Tu dois être connecté en tant que commerçant pour accéder à cette vue.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/me/pro/bank`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json().catch(() => ({}));
        if (r.status === 403) {
          setError("Accès réservé aux comptes commerçants validés (role=commerce).");
        } else if (!r.ok || !data.ok) {
          setError(data.error || `Erreur ${r.status}`);
        } else {
          setBank(data);
        }
      } catch (_) {
        setError("Erreur réseau");
      } finally {
        setLoading(false);
      }
    })();
    // v3.4.0 — Fetch aussi la config domain-aware pour dériver les modules
    // spécifiques au métier du pro (avocat vs fleuriste vs hôtelier).
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/me/pro/space-config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.ok) setSpaceConfig(data);
      } catch (_) { /* silencieux — les modules ne sont qu'un enrichissement */ }
    })();
    // v3.6.1 — slug boutique pour le CTA « Voir ma boutique » de la section.
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/me/minisite`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.ok && data.slug) setShopSlug(data.slug);
      } catch (_) { /* silencieux */ }
    })();
  }, []);

  const onCopyIban = async () => {
    if (!bank?.iban) return;
    try {
      await navigator.clipboard.writeText(bank.iban);
      toast.success("📋 IBAN copié");
    } catch (_) { toast.error("Copie impossible"); }
  };

  if (loading) {
    return <div style={{ color: "#94a3b8", fontSize: 13, padding: 20 }} data-testid="mc-pro-loading">Chargement du compte pro…</div>;
  }
  if (error) {
    return (
      <div data-testid="mc-pro-error" style={{
        background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: 12, padding: 18, color: "#FCA5A5", fontSize: 13,
      }}>
        <strong>⛔ Accès refusé</strong><br />{error}
      </div>
    );
  }

  return (
    <div data-testid="mc-pro-root" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ─── Bandeau header ─── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        background: "linear-gradient(135deg, rgba(15,26,46,0.6), rgba(27,46,84,0.3))",
        border: "1px solid rgba(212,175,55,0.2)", borderRadius: 14, padding: "14px 18px",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: "#F4F1E8" }}>Mon Compte Pro</h2>
          <div style={{ fontSize: 11, color: "rgba(244,241,232,0.55)", marginTop: 2 }}>
            En partenariat avec <strong style={{ color: "#D4AF37" }}>Bridge Bank Group</strong>
            {spaceConfig?.label && (
              <> · <span data-testid="mc-pro-domain-label" style={{ color: "#10B981" }}>{spaceConfig.label}</span></>
            )}
          </div>
        </div>
        <div style={{ fontSize: 11, color: "rgba(244,241,232,0.5)" }}>
          {bank?.account_holder}
        </div>
      </div>

      {/* ─── 🏪 MA BOUTIQUE — l'espace pro est CONNECTÉ à la boutique publique ─── */}
      <ProSection
        testid="mc-pro-section-boutique"
        icon="🏪"
        title="Ma boutique"
        subtitle="Fiche, photos et produits — publiés en direct sur ta page publique et cherchables dans le chat AYA."
        action={shopSlug && (
          <a
            href={`/commercant/${shopSlug}`}
            data-testid="mc-pro-view-shop"
            style={{
              fontSize: 12, fontWeight: 800, color: "#062A1F", textDecoration: "none",
              background: "linear-gradient(135deg, #34D399, #10B981)", padding: "8px 14px",
              borderRadius: 999, boxShadow: "0 4px 14px rgba(16,185,129,0.3)",
            }}
          >Voir ma boutique →</a>
        )}
      >
        <MiniSiteEditor />
      </ProSection>

      {/* ─── 📊 MON ACTIVITÉ ─── */}
      <ProSection
        testid="mc-pro-section-activite"
        icon="📊"
        title="Mon activité"
        subtitle="Ventes, indicateurs et commandes à traiter."
      >
        <MerchantProDashboard bank={bank} />
        <OrdersSection />
      </ProSection>

      {/* ─── 📥 DEMANDES CLIENTS — leads routés depuis le chat AYA ─── */}
      <ProSection
        testid="mc-pro-section-leads"
        icon="📥"
        title="Demandes clients"
        subtitle="Besoins décrits par les clients dans le chat AYA et routés vers ton métier."
      >
        <ProLeadsSection />
        {/* v3.24.0 — Réservations beauté reçues (démo). */}
        <ProBookingsSection />
      </ProSection>

      {/* ─── 💳 MA BANQUE ─── */}
      <ProSection
        testid="mc-pro-section-banque"
        icon="💳"
        title="Ma banque"
        subtitle="Carte virtuelle Bridge Bank et historique des transactions."
      >
        <VirtualCard
          card={bank?.card}
          holder={bank?.account_holder}
          iban={bank?.iban}
          cvvRevealed={cvvRevealed}
          onRevealCvv={() => setCvvRevealed((v) => !v)}
          onCopyIban={onCopyIban}
        />
        <TransactionsSection />
      </ProSection>

      {/* ─── 🚀 OUTILS PRO ─── */}
      <ProSection
        testid="mc-pro-section-outils"
        icon="🚀"
        title="Outils pro"
        subtitle="Live commerce et modules adaptés à ton métier."
      >
        <AyaLiveShortcut />
        {/* v4.8 — Accès direct Mes catalogues (Kiosque) */}
        <a
          href="/pro/catalogues"
          data-testid="pro-catalogs-shortcut"
          style={{
            display: "flex", alignItems: "center", gap: 12, textDecoration: "none",
            background: "linear-gradient(135deg, rgba(251,191,36,0.14), rgba(255,126,27,0.10))",
            border: "1px solid rgba(251,191,36,0.4)", borderRadius: 16, padding: 18, marginTop: 12,
          }}
        >
          <span style={{ fontSize: 26 }}>🏪</span>
          <span style={{ flex: 1 }}>
            <span style={{ display: "block", color: "#fbbf24", fontWeight: 800, fontSize: 14.5 }}>Mes catalogues</span>
            <span style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: 11.5 }}>Créez et publiez vos catalogues sur le Kiosque AYA</span>
          </span>
          <span style={{ color: "#fbbf24", fontWeight: 800 }}>›</span>
        </a>
        {spaceConfig && spaceConfig.modules && spaceConfig.modules.length > 0 && (
          <div data-testid="mc-pro-modules-section">
            <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(232,213,163,0.72)", marginBottom: 10 }}>
              Modules {spaceConfig.label}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
              {spaceConfig.modules.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  data-testid={`mc-pro-module-${m.key}`}
                  onClick={() => {
                    if (m.path === "/pro/live") { window.location.href = m.path; return; }
                    toast.info(`Module « ${m.label} » — bientôt disponible.`);
                  }}
                  style={{
                    background: "rgba(11,69,49,0.5)",
                    border: "1px solid rgba(232,213,163,0.18)",
                    borderRadius: 12,
                    padding: "12px 10px",
                    color: "#F4F1E8",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "border-color 0.15s ease, transform 0.15s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(232,213,163,0.18)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{m.icon}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{m.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </ProSection>

      {/* ─── 📍 MON ADRESSE PRO ─── */}
      <ProSection
        testid="mc-pro-section-adresse"
        icon="📍"
        title="Mon adresse pro"
        subtitle="L'adresse CIV où tes clients et livreurs te trouvent."
      >
        <ProAddressSection addr={bank?.pro_address} />
      </ProSection>

      {/* ─── Footer roadmap ─── */}
      <div style={{
        textAlign: "center", fontSize: 11, color: "rgba(244,241,232,0.4)", fontStyle: "italic", marginTop: 8,
      }}>
        ✨ Plus de fonctionnalités arrivent en v2.19.39 (transactions complètes) et v2.19.40 (capacité crédit, retraits).
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// v2.19.39 Sprint 2 — Section Commandes commerçant
// ───────────────────────────────────────────────────────────
function OrdersSection() {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    const statusParam = tab === "pending" ? "pending" : (tab === "en_cours" ? "assigned,en_route" : "delivered");
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/me/pro/orders?status=${statusParam}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (j.ok) setOrders(j.orders || []);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, [tab]);

  const dispatchCourier = (orderRef) => {
    toast.info(`🚴 Dispatch livreur lancé pour ${orderRef} (mock)`);
  };

  return (
    <div data-testid="orders-section">
      <h3 style={{ margin: "4px 0 10px", fontSize: 15, color: "#F4F1E8" }}>📦 Mes commandes</h3>
      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
        {[
          { id: "pending", label: "À dispatcher" },
          { id: "en_cours", label: "En cours" },
          { id: "delivered", label: "Livrées" },
        ].map((t) => (
          <button key={t.id} data-testid={`orders-tab-${t.id}`} onClick={() => setTab(t.id)} style={{
            background: tab === t.id ? "linear-gradient(135deg,#D4AF37,#A07C20)" : "rgba(255,255,255,0.05)",
            color: tab === t.id ? "#0F1A2E" : "rgba(244,241,232,0.7)",
            border: "1px solid rgba(212,175,55,0.2)", padding: "6px 12px", borderRadius: 999,
            fontWeight: 700, fontSize: 11, cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>
      {loading && <div style={{ fontSize: 12, color: "#94a3b8" }}>Chargement…</div>}
      {!loading && orders.length === 0 && (
        <div style={{ padding: 14, background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 12, color: "rgba(244,241,232,0.45)", fontStyle: "italic", textAlign: "center" }}>
          Aucune commande dans cette catégorie.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {orders.map((o) => (
          <div key={o.order_ref} data-testid={`order-${o.order_ref}`} style={{
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 11, color: "rgba(244,241,232,0.5)", fontFamily: "ui-monospace,monospace", marginBottom: 2 }}>{o.order_ref}</div>
              <div style={{ fontSize: 13, color: "#F4F1E8" }}>{o.pickup} → <strong style={{ color: "#D4AF37" }}>{o.drop}</strong></div>
              <div style={{ fontSize: 11, color: "rgba(244,241,232,0.55)", marginTop: 2 }}>Client : {o.client_pseudo} · {fmtFcfa(o.amount_fcfa)} <span style={{ color: "rgba(239,68,68,0.6)" }}>(- {fmtFcfa(o.commission_fcfa)} commission)</span></div>
            </div>
            {o.status === "pending" && (
              <button data-testid={`dispatch-${o.order_ref}`} onClick={() => dispatchCourier(o.order_ref)} style={{
                background: "linear-gradient(135deg,#D4AF37,#A07C20)", color: "#0F1A2E", border: "none",
                padding: "8px 12px", borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: "pointer",
              }}>🚴 Dispatcher</button>
            )}
            {(o.status === "assigned" || o.status === "en_route") && (
              <a href={`/track/${o.order_ref}`} style={{
                background: "rgba(59,130,246,0.15)", color: "#60A5FA", border: "1px solid rgba(59,130,246,0.4)",
                padding: "8px 12px", borderRadius: 8, fontWeight: 700, fontSize: 11, textDecoration: "none",
              }}>📍 Suivre</a>
            )}
            {o.status === "delivered" && (
              <a href={`/replay/${o.order_ref}`} style={{
                background: "rgba(16,185,129,0.15)", color: "#34D399", border: "1px solid rgba(16,185,129,0.4)",
                padding: "8px 12px", borderRadius: 8, fontWeight: 700, fontSize: 11, textDecoration: "none",
              }}>🎬 Replay</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// v2.19.39 Sprint 2 — Section Historique transactions
// ───────────────────────────────────────────────────────────
function TransactionsSection() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState({ transactions: [], totals_period: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/me/pro/transactions?period=${period}&per_page=20`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (j.ok) setData(j);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, [period]);

  const catColor = (cat) => ({ payment: "#10B981", commission: "#EF4444", withdraw: "#F59E0B", credit: "#3B82F6" }[cat] || "#94A3B8");
  const catIcon = (cat) => ({ payment: "💰", commission: "📤", withdraw: "🏦", credit: "💳" }[cat] || "🔵");

  return (
    <div data-testid="transactions-section" style={{ marginTop: 4 }}>
      <h3 style={{ margin: "4px 0 10px", fontSize: 15, color: "#F4F1E8" }}>💸 Historique transactions</h3>
      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
        {[
          { id: "today", label: "Aujourd'hui" },
          { id: "7d", label: "7 jours" },
          { id: "30d", label: "30 jours" },
          { id: "all", label: "Tout" },
        ].map((p) => (
          <button key={p.id} data-testid={`tx-period-${p.id}`} onClick={() => setPeriod(p.id)} style={{
            background: period === p.id ? "linear-gradient(135deg,#D4AF37,#A07C20)" : "rgba(255,255,255,0.05)",
            color: period === p.id ? "#0F1A2E" : "rgba(244,241,232,0.7)",
            border: "1px solid rgba(212,175,55,0.2)", padding: "5px 11px", borderRadius: 999,
            fontWeight: 700, fontSize: 11, cursor: "pointer",
          }}>{p.label}</button>
        ))}
      </div>
      {data.totals_period && (
        <div data-testid="tx-totals" style={{
          display: "flex", gap: 10, marginBottom: 10, padding: "10px 12px",
          background: "rgba(15,26,46,0.6)", borderRadius: 10, border: "1px solid rgba(212,175,55,0.2)",
          flexWrap: "wrap", fontSize: 12,
        }}>
          <div><span style={{ color: "#10B981" }}>Entrées :</span> <strong style={{ color: "#F4F1E8" }}>{fmtFcfa(data.totals_period.in_fcfa)}</strong></div>
          <div><span style={{ color: "#EF4444" }}>Sorties :</span> <strong style={{ color: "#F4F1E8" }}>{fmtFcfa(data.totals_period.out_fcfa)}</strong></div>
          <div><span style={{ color: "#D4AF37" }}>Net :</span> <strong style={{ color: data.totals_period.net_fcfa >= 0 ? "#10B981" : "#EF4444" }}>{fmtFcfa(data.totals_period.net_fcfa)}</strong></div>
        </div>
      )}
      {loading && <div style={{ fontSize: 12, color: "#94a3b8" }}>Chargement…</div>}
      {!loading && data.transactions.length === 0 && (
        <div style={{ padding: 14, background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 12, color: "rgba(244,241,232,0.45)", fontStyle: "italic", textAlign: "center" }}>
          Aucune transaction sur cette période.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {data.transactions.map((t) => (
          <div key={t.id} style={{
            display: "flex", justifyContent: "space-between", padding: "8px 12px",
            background: "rgba(255,255,255,0.03)", borderRadius: 8, borderLeft: `3px solid ${catColor(t.category)}`,
            fontSize: 12, gap: 10, alignItems: "center",
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14 }}>{catIcon(t.category)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#F4F1E8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.label}</div>
                <div style={{ fontSize: 10, color: "rgba(244,241,232,0.45)" }}>{t.date}</div>
              </div>
            </div>
            <div style={{ fontWeight: 700, color: t.amount_fcfa > 0 ? "#10B981" : "#EF4444", whiteSpace: "nowrap" }}>
              {t.amount_fcfa > 0 ? "+" : ""}{fmtFcfa(t.amount_fcfa)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

